import { useEffect, useState } from "react";
import { ArrowRight, Check, Package, AlertCircle, Clock, XCircle, PlayCircle, ListChecks } from "lucide-react";
import SEO from "@/components/SEO";
import { ASSETS, THERABODY_PRODUCTS } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";
import { fetchProductOverrides } from "@/lib/api";
import ProductRequestModal from "@/components/ProductRequestModal";

const STOCK_META = {
  in_stock:     { label: "In Stock",      tone: "good",    Icon: Package },
  limited:      { label: "Limited Stock", tone: "warn",    Icon: AlertCircle },
  backorder:    { label: "On Backorder",  tone: "warn",    Icon: Clock },
  out_of_stock: { label: "Out of Stock",  tone: "bad",     Icon: XCircle },
};

function StockBadge({ status, note }) {
  const meta = STOCK_META[status];
  if (!meta) return null;
  const { label, tone, Icon } = meta;
  const toneClass = {
    good: "border-[#0F766E] bg-[#ECFDF5] text-[#0F766E]",
    warn: "border-[#92400E] bg-[#FEF3C7] text-[#92400E]",
    bad:  "border-[#991B1B] bg-[#FEE2E2] text-[#991B1B]",
  }[tone];
  return (
    <div className="flex items-center gap-2 mt-3" data-testid={`stock-${status}`}>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-xs uppercase tracking-[0.15em] font-bold ${toneClass}`}>
        <Icon size={12} /> {label}
      </span>
      {note && <span className="text-xs text-[#6B7280]">— {note}</span>}
    </div>
  );
}

function VideoEmbed({ url }) {
  if (!url) return null;
  // Normalise YouTube + Vimeo URLs to embed format.
  let embed = url;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) embed = `https://www.youtube.com/embed/${id}`;
    } else if (u.hostname === "youtu.be") {
      embed = `https://www.youtube.com/embed${u.pathname}`;
    } else if (u.hostname.includes("vimeo.com")) {
      embed = `https://player.vimeo.com/video${u.pathname}`;
    }
  } catch (e) { /* leave as-is */ }
  return (
    <div className="mt-10" data-testid="how-to-video">
      <div className="ash-eyebrow inline-flex items-center gap-2"><PlayCircle size={14} /> How to use it</div>
      <div className="mt-3 aspect-video w-full max-w-2xl bg-black">
        <iframe
          src={embed}
          title="How to use this Therabody product"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

function CareGuide({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="mt-8 p-6 border border-[#e5e7eb] bg-white" data-testid="care-guide">
      <div className="ash-eyebrow inline-flex items-center gap-2"><ListChecks size={14} /> Care &amp; cleaning</div>
      <ol className="mt-4 flex flex-col gap-2.5 list-decimal pl-5 text-sm text-[#3C4048] leading-relaxed">
        {steps.map((s, i) => (<li key={i}>{s}</li>))}
      </ol>
    </div>
  );
}

const productListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: THERABODY_PRODUCTS.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Product",
      name: p.name,
      description: p.description,
      image: p.image,
      brand: { "@type": "Brand", name: "Therabody" },
      url: absoluteUrl(`/therabody#${p.slug}`),
    },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    {
      "@type": "ListItem",
      position: 2,
      name: "Therabody Products",
      item: absoluteUrl("/therabody"),
    },
  ],
};

export default function Therabody() {
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    fetchProductOverrides()
      .then((d) => setOverrides(d || {}))
      .catch(() => setOverrides({}));
  }, []);

  return (
    <main data-testid="therabody-page" className="bg-white">
      <SEO
        title="Therabody Products — Official Bermuda Reseller"
        description="Therabody recovery technology, prescribed by Ashlan Clinic's Therapists. Theragun, JetBoots, SmartGoggles, SleepMask and more — fitted to your treatment plan."
        path="/therabody"
        schemas={[productListSchema, breadcrumbSchema]}
      />
      <section className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-28">
          <div className="grid md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-7">
              <img
                src={ASSETS.therabodyLogo}
                alt="Therabody"
                className="h-10 object-contain invert opacity-90"
              />
              <h1 className="mt-8 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-white">
                Therabody, prescribed not sold.
              </h1>
            </div>
            <div className="md:col-span-5">
              <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#9DD4D8]">
                Official Bermuda Reseller
              </span>
              <p className="mt-4 text-lg text-[#D1D5DB] leading-relaxed">
                Our therapists select and fit each product to your treatment
                plan. This catalogue exists to help you understand what we
                recommend and why — there is no online cart and no checkout.
                Submit a request and we’ll be in touch.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col gap-20 md:gap-28">
          {THERABODY_PRODUCTS.map((p, i) => {
            const ov = overrides[p.slug] || {};
            return (
            <article
              key={p.slug}
              id={p.slug}
              data-testid={`therabody-product-detail-${i}`}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-start ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6">
                <div className="aspect-[4/3] overflow-hidden bg-[#F5F5F5]">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="lg:col-span-6">
                <span className="ash-eyebrow">
                  Product {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-[#080808]">
                  {p.name}
                </h2>
                <StockBadge status={ov.stock_status} note={ov.stock_note} />
                <p className="mt-6 text-[#3C4048] text-lg leading-relaxed">
                  {p.description}
                </p>

                <div className="mt-10 grid sm:grid-cols-2 gap-8">
                  <div>
                    <div className="ash-eyebrow">Benefits</div>
                    <ul className="mt-4 flex flex-col gap-3">
                      {p.benefits.map((b, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 text-[#080808]"
                          data-testid={`product-${i}-benefit-${idx}`}
                        >
                          <Check
                            size={18}
                            strokeWidth={2}
                            className="text-[#3A7A8C] mt-1 flex-shrink-0"
                          />
                          <span className="text-[#3C4048]">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="ash-eyebrow">Ideal For</div>
                    <ul className="mt-4 flex flex-col gap-3">
                      {p.idealFor.map((b, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 text-[#080808]"
                          data-testid={`product-${i}-ideal-${idx}`}
                        >
                          <span className="ash-rule mt-3 flex-shrink-0" />
                          <span className="text-[#3C4048]">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <a
                    href="#how-it-helps"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(`how-${p.slug}`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    data-testid={`product-${i}-see-how-button`}
                    className="ash-btn-secondary"
                  >
                    See How It Can Help You
                  </a>

                  <ProductRequestModal
                    productName={p.name}
                    trigger={
                      <button
                        type="button"
                        data-testid={`product-${i}-request-button`}
                        className="ash-btn-primary"
                      >
                        Request This Product
                        <ArrowRight size={16} />
                      </button>
                    }
                  />
                </div>

                <div
                  id={`how-${p.slug}`}
                  className="mt-12 p-8 bg-[#F5F5F5] border-l-2 border-[#5FA8B4]"
                >
                  <div className="ash-eyebrow">How It Fits Your Treatment Plan</div>
                  <p className="mt-3 text-[#080808] font-medium leading-relaxed">
                    Your therapist will demonstrate this product during a
                    session, fit it to your specific protocol and show you
                    exactly how to use it between visits. Buying a product
                    without a plan rarely produces lasting change — pairing it
                    with hands-on care does.
                  </p>
                </div>

                <VideoEmbed url={ov.how_to_video_url} />
                <CareGuide steps={ov.care_guide} />
              </div>
            </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
